/**
 * WebRTC Voice Client for OpenAI Realtime API
 *
 * This client establishes a WebRTC connection to OpenAI's Realtime API,
 * handles audio streaming, and processes function calls for item extraction.
 */

export type VoiceClientState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnecting'
  | 'error';

export interface VoiceClientConfig {
  ephemeralKey: string;
  model: string;
  voice: string;
  instructions: string;
  inventoryId: string;
  accessToken: string;
  tools: Array<{
    type: 'function';
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  }>;
}

export interface ExtractedItemEvent {
  itemId: string;
  title: string;
  category: string;
  confidence: number;
}

export interface VoiceClientEvents {
  onStateChange: (state: VoiceClientState) => void;
  onItemExtracted: (item: ExtractedItemEvent) => void;
  onTranscript: (text: string, isFinal: boolean) => void;
  onError: (error: Error) => void;
  onAudioLevel: (level: number) => void;
}

const OPENAI_REALTIME_URL = 'https://api.openai.com/v1/realtime';

export class VoiceClient {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private state: VoiceClientState = 'idle';
  private events: VoiceClientEvents;
  private config: VoiceClientConfig | null = null;
  private audioLevelInterval: number | null = null;

  constructor(events: VoiceClientEvents) {
    this.events = events;
  }

  getState(): VoiceClientState {
    return this.state;
  }

  private setState(state: VoiceClientState) {
    this.state = state;
    this.events.onStateChange(state);
  }

  async connect(config: VoiceClientConfig): Promise<void> {
    if (this.state !== 'idle') {
      throw new Error('Client is not in idle state');
    }

    this.config = config;
    this.setState('connecting');

    try {
      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 24000,
        },
      });

      // Set up audio analysis for level monitoring
      this.setupAudioAnalysis();

      // Create peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers: [], // OpenAI handles ICE
      });

      // Add audio track
      const audioTrack = this.mediaStream.getAudioTracks()[0];
      if (audioTrack) {
        this.peerConnection.addTrack(audioTrack, this.mediaStream);
      }

      // Handle incoming audio from OpenAI
      this.peerConnection.ontrack = (event) => {
        const audio = new Audio();
        audio.srcObject = event.streams[0] ?? null;
        audio.play().catch(console.error);
      };

      // Create data channel for events
      this.dataChannel = this.peerConnection.createDataChannel('oai-events');
      this.setupDataChannel();

      // Create and set local description
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Send offer to OpenAI and get answer
      const sdpResponse = await this.sendOfferToOpenAI(offer.sdp!);

      // Set remote description
      await this.peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: sdpResponse,
      });

      // Wait for connection
      await this.waitForConnection();

      // Send session configuration
      this.sendSessionConfig();

      this.setState('connected');
    } catch (error) {
      this.setState('error');
      this.events.onError(error instanceof Error ? error : new Error(String(error)));
      await this.disconnect();
      throw error;
    }
  }

  private setupAudioAnalysis() {
    if (!this.mediaStream) return;

    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;

    const source = this.audioContext.createMediaStreamSource(this.mediaStream);
    source.connect(this.analyser);

    // Monitor audio levels
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    this.audioLevelInterval = window.setInterval(() => {
      if (!this.analyser) return;
      this.analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const level = Math.min(average / 128, 1); // Normalize to 0-1
      this.events.onAudioLevel(level);
    }, 100);
  }

  private setupDataChannel() {
    if (!this.dataChannel) return;

    this.dataChannel.onopen = () => {
      console.log('Data channel opened');
    };

    this.dataChannel.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleServerEvent(message);
      } catch (error) {
        console.error('Failed to parse server event:', error);
      }
    };

    this.dataChannel.onerror = (error) => {
      console.error('Data channel error:', error);
      this.events.onError(new Error('Data channel error'));
    };

    this.dataChannel.onclose = () => {
      console.log('Data channel closed');
    };
  }

  private async sendOfferToOpenAI(sdp: string): Promise<string> {
    if (!this.config) throw new Error('No config');

    const response = await fetch(`${OPENAI_REALTIME_URL}?model=${this.config.model}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.config.ephemeralKey}`,
        'Content-Type': 'application/sdp',
      },
      body: sdp,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to connect to OpenAI: ${response.status} - ${error}`);
    }

    return response.text();
  }

  private async waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.peerConnection) {
        reject(new Error('No peer connection'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);

      this.peerConnection.oniceconnectionstatechange = () => {
        const state = this.peerConnection?.iceConnectionState;
        console.log('ICE connection state:', state);

        if (state === 'connected' || state === 'completed') {
          clearTimeout(timeout);
          resolve();
        } else if (state === 'failed' || state === 'disconnected') {
          clearTimeout(timeout);
          reject(new Error(`Connection failed: ${state}`));
        }
      };

      // Check if already connected
      if (this.peerConnection.iceConnectionState === 'connected') {
        clearTimeout(timeout);
        resolve();
      }
    });
  }

  private sendSessionConfig() {
    if (!this.dataChannel || !this.config) return;

    // Configure the session
    this.sendEvent({
      type: 'session.update',
      session: {
        modalities: ['text', 'audio'],
        instructions: this.config.instructions,
        voice: this.config.voice,
        input_audio_format: 'pcm16',
        output_audio_format: 'pcm16',
        input_audio_transcription: {
          model: 'whisper-1',
        },
        tools: this.config.tools,
        tool_choice: 'auto',
      },
    });
  }

  private sendEvent(event: Record<string, unknown>) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      console.warn('Data channel not ready, cannot send event');
      return;
    }

    this.dataChannel.send(JSON.stringify(event));
  }

  private handleServerEvent(event: Record<string, unknown>) {
    const eventType = event.type as string;

    // Log all events for debugging
    console.log('Realtime event:', eventType, event);

    switch (eventType) {
      case 'session.created':
      case 'session.updated':
        console.log('Session event:', eventType);
        break;

      case 'conversation.item.input_audio_transcription.completed':
        // User's speech transcribed
        const transcript = (event as any).transcript as string;
        if (transcript) {
          this.events.onTranscript(transcript, true);
        }
        break;

      case 'response.function_call_arguments.done':
        // Function call completed, handle it
        console.log('Function call received!', event);
        this.handleFunctionCall(event);
        break;

      case 'response.output_item.done':
        // Function calls are handled via response.function_call_arguments.done
        // Don't handle them again here to avoid duplicates
        break;

      case 'response.audio_transcript.delta':
        // AI is speaking - partial transcript
        const delta = (event as any).delta as string;
        if (delta) {
          this.events.onTranscript(delta, false);
        }
        break;

      case 'error':
        const errorMsg = (event as any).error?.message || 'Unknown error';
        console.error('Realtime error:', event);
        this.events.onError(new Error(errorMsg));
        break;

      default:
        break;
    }
  }

  private async handleFunctionCall(event: Record<string, unknown>) {
    const callId = (event as any).call_id as string;
    const name = (event as any).name as string;
    const args = JSON.parse((event as any).arguments || '{}');

    console.log('Function call:', name, args);

    if (name === 'add_inventory_item') {
      try {
        // Call our backend to persist the item
        const response = await fetch('/api/v1/items', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.config!.accessToken}`,
          },
          body: JSON.stringify({
            inventoryId: this.config!.inventoryId,
            title: args.title,
            description: args.description || '',
            category: args.category || 'Other',
            subCategory: args.subCategory,
            attributes: {
              brand: args.brand,
              model: args.model,
              condition: args.condition,
              location: args.location,
              quantity: args.quantity || 1,
              estimatedValue: args.estimatedValue,
            },
          }),
        });

        console.log('Item creation response:', response.status);

        if (response.ok) {
          const result = await response.json();

          // Notify UI of extracted item
          this.events.onItemExtracted({
            itemId: result.data.id,
            title: args.title,
            category: args.category,
            confidence: 0.8, // Will be calculated by backend
          });

          // Send success result back to OpenAI
          this.sendFunctionResult(callId, {
            success: true,
            itemId: result.data.id,
            message: `Added "${args.title}" to inventory`,
          });
        } else {
          this.sendFunctionResult(callId, {
            success: false,
            error: 'Failed to save item',
          });
        }
      } catch (error) {
        console.error('Failed to handle function call:', error);
        this.sendFunctionResult(callId, {
          success: false,
          error: 'Failed to process item',
        });
      }
    } else {
      // Handle other function calls
      this.sendFunctionResult(callId, {
        success: true,
        message: 'Function handled',
      });
    }
  }

  private sendFunctionResult(callId: string, result: Record<string, unknown>) {
    this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(result),
      },
    });

    // Trigger response generation after function result
    this.sendEvent({
      type: 'response.create',
    });
  }

  async disconnect(): Promise<void> {
    if (this.state === 'idle') return;

    this.setState('disconnecting');

    // Stop audio level monitoring
    if (this.audioLevelInterval) {
      clearInterval(this.audioLevelInterval);
      this.audioLevelInterval = null;
    }

    // Close audio context
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
      this.analyser = null;
    }

    // Stop media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Close data channel
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.config = null;
    this.setState('idle');
  }
}
